import type { RouteObject } from "react-router";

import type { Area } from "@pharmacy/domain";

import { OwnerOnly, RequireArea } from "@/auth/guards";

/**
 * What both apps' routers are made of (app/router.tsx for FarmashiBook,
 * dukaanbook/router.tsx for DukaanBook): the permission check around a page,
 * and the signed-out pages they share. Kept apart from either router so
 * neither app's bundle carries the other's address list.
 */

type Who = Area | "owners" | "everyone";

/** Wraps a page in its permission check: refused people see "no access", not the page. */
function guarded(route: RouteObject, who: Who): RouteObject {
  if (who === "everyone") return route;
  const element = who === "owners" ? <OwnerOnly /> : <RequireArea area={who} />;
  return { element, children: [route] };
}

/**
 * A built page, loaded the first time it is opened: `load` imports its
 * module and picks the component out of it.
 */
export function page(path: string, load: () => Promise<React.ComponentType>, who: Who = "everyone"): RouteObject {
  return guarded({ path, lazy: async () => ({ Component: await load() }) }, who);
}

export const publicPages: RouteObject[] = [
  page("/welcome", async () => (await import("@/features/auth/pages/welcome-page")).WelcomePage),
  page("/sign-in/owner", async () => (await import("@/features/auth/pages/sign-in-owner-page")).SignInOwnerPage),
  page("/sign-in/staff", async () => (await import("@/features/auth/pages/sign-in-staff-page")).SignInStaffPage),
  page("/sign-up", async () => (await import("@/features/auth/pages/sign-up-page")).SignUpPage),
  page("/forgot-password", async () => (await import("@/features/auth/pages/forgot-password-page")).ForgotPasswordPage),
  page("/accept-invite", async () => (await import("@/features/auth/pages/accept-invite-page")).AcceptInvitePage),
];
